import {
  createContext,
  useContext,
  useReducer,
  useCallback,
  useEffect,
  useRef,
  type ReactNode,
  type Dispatch,
} from 'react';

export type ToastType = 'success' | 'error' | 'loading' | 'info';

export interface ToastItem {
  id: string;
  message: string;
  type: ToastType;
  autoDismiss: boolean;
}

// ─── Actions ──────────────────────────────────────────────────────────────────

type ToastAction =
  | { type: 'ADD_TOAST'; payload: ToastItem }
  | { type: 'REMOVE_TOAST'; payload: { id: string } }
  | { type: 'UPDATE_TOAST'; payload: { id: string; message?: string; type?: ToastType } };

// ─── Reducer ──────────────────────────────────────────────────────────────────

function toastReducer(state: ToastItem[], action: ToastAction): ToastItem[] {
  switch (action.type) {
    case 'ADD_TOAST':
      return [...state, action.payload];

    case 'REMOVE_TOAST':
      return state.filter((t) => t.id !== action.payload.id);

    case 'UPDATE_TOAST':
      return state.map((t) => {
        if (t.id !== action.payload.id) return t;
        return {
          ...t,
          ...(action.payload.message !== undefined ? { message: action.payload.message } : {}),
          ...(action.payload.type !== undefined ? { type: action.payload.type } : {}),
          // auto-dismiss if updated to success/info; keep false for error/loading
          autoDismiss:
            action.payload.type !== undefined
              ? action.payload.type === 'success' || action.payload.type === 'info'
              : t.autoDismiss,
        };
      });

    default:
      return state;
  }
}

// ─── Context ──────────────────────────────────────────────────────────────────

interface ToastContextValue {
  toasts: ToastItem[];
  dispatch: Dispatch<ToastAction>;
}

const ToastStoreContext = createContext<ToastContextValue | null>(null);

interface ToastApiContextValue {
  showToast: (message: string, type?: ToastType) => string;
  removeToast: (id: string) => void;
  updateToast: (id: string, update: { message?: string; type?: ToastType }) => void;
}

const ToastApiContext = createContext<ToastApiContextValue | null>(null);

// ─── Provider ─────────────────────────────────────────────────────────────────

let idCounter = 0;

function generateId(): string {
  idCounter += 1;
  return `toast-${Date.now()}-${idCounter}`;
}

const AUTO_DISMISS_MS = 5000;

interface ToastProviderProps {
  children: ReactNode;
}

export function ToastProvider({ children }: ToastProviderProps) {
  const [toasts, dispatch] = useReducer(toastReducer, []);

  // Track active auto-dismiss timers so we can cancel them on unmount
  const timersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  const removeToast = useCallback((id: string): void => {
    // Cancel any pending auto-dismiss timer for this toast
    const timer = timersRef.current.get(id);
    if (timer !== undefined) {
      clearTimeout(timer);
      timersRef.current.delete(id);
    }
    dispatch({ type: 'REMOVE_TOAST', payload: { id } });
  }, []);

  const scheduleAutoDismiss = useCallback(
    (id: string): void => {
      // Cancel existing timer if rescheduling
      const existing = timersRef.current.get(id);
      if (existing !== undefined) {
        clearTimeout(existing);
      }
      const timer = setTimeout(() => {
        timersRef.current.delete(id);
        dispatch({ type: 'REMOVE_TOAST', payload: { id } });
      }, AUTO_DISMISS_MS);
      timersRef.current.set(id, timer);
    },
    [],
  );

  const showToast = useCallback(
    (message: string, type: ToastType = 'info'): string => {
      const id = generateId();
      const autoDismiss = type === 'success' || type === 'info';
      dispatch({
        type: 'ADD_TOAST',
        payload: { id, message, type, autoDismiss },
      });
      if (autoDismiss) {
        scheduleAutoDismiss(id);
      }
      return id;
    },
    [scheduleAutoDismiss],
  );

  const updateToast = useCallback(
    (id: string, update: { message?: string; type?: ToastType }): void => {
      dispatch({ type: 'UPDATE_TOAST', payload: { id, ...update } });
      // If updated type is auto-dismiss, schedule or reschedule
      if (update.type === 'success' || update.type === 'info') {
        scheduleAutoDismiss(id);
      } else if (update.type === 'error' || update.type === 'loading') {
        // Cancel auto-dismiss for error/loading
        const timer = timersRef.current.get(id);
        if (timer !== undefined) {
          clearTimeout(timer);
          timersRef.current.delete(id);
        }
      }
    },
    [scheduleAutoDismiss],
  );

  // Clean up all timers on unmount
  useEffect(() => {
    const timers = timersRef.current;
    return (): void => {
      timers.forEach((timer) => clearTimeout(timer));
      timers.clear();
    };
  }, []);

  const storeValue: ToastContextValue = { toasts, dispatch };
  const apiValue: ToastApiContextValue = { showToast, removeToast, updateToast };

  return (
    <ToastStoreContext.Provider value={storeValue}>
      <ToastApiContext.Provider value={apiValue}>
        {children}
      </ToastApiContext.Provider>
    </ToastStoreContext.Provider>
  );
}

// ─── Hooks ────────────────────────────────────────────────────────────────────

/**
 * Returns the raw toast store (toasts array + dispatch).
 * Used by ToastContainer to read and render toasts.
 */
export function useToastStore(): ToastContextValue {
  const ctx = useContext(ToastStoreContext);
  if (ctx === null) {
    throw new Error('useToastStore must be used within a ToastProvider');
  }
  return ctx;
}

/**
 * Returns the toast action API: showToast, removeToast, updateToast.
 * Used by page components to trigger toasts.
 */
export function useToast(): ToastApiContextValue {
  const ctx = useContext(ToastApiContext);
  if (ctx === null) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return ctx;
}
